import { useEffect, useMemo, useState, type JSX } from 'react';
import { getSprintConfig, listDesigners } from './api';
import { listTickets, type Ticket } from './ticketsApi';
import { DEFAULT_EPOCH_MONDAY_UTC, sprintIndexFromMs, sprintLabel } from './sprints';

type Row = { user: string; count: number };

// Designer leaderboard: who closed the most tickets. Two tables - all-time
// totals and a single selectable sprint. "Done" is the completed status; a
// ticket is credited to its current assignee.
export function LeaderboardView(): JSX.Element {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [designers, setDesigners] = useState<string[]>([]);
  const [epochMs, setEpochMs] = useState(DEFAULT_EPOCH_MONDAY_UTC);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const nowMs = Date.now();
  const currentSprint = sprintIndexFromMs(nowMs, epochMs);
  const [selectedSprint, setSelectedSprint] = useState(currentSprint);

  useEffect(() => {
    listTickets()
      .then((t) => {
        setTickets(t);
        setError('');
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
    listDesigners()
      .then(setDesigners)
      .catch(() => {});
    getSprintConfig()
      .then((c) => {
        const ms = c?.epochMondayMs ?? DEFAULT_EPOCH_MONDAY_UTC;
        setEpochMs(ms);
        setSelectedSprint(sprintIndexFromMs(nowMs, ms));
      })
      .catch(() => {});
    // nowMs is intentionally captured once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const done = useMemo(() => tickets.filter((t) => t.status === 'done'), [tickets]);

  const sprintOf = (t: Ticket): number =>
    t.sprintIndex ?? sprintIndexFromMs(t.createdAtMs ?? nowMs, epochMs);

  // Tally done tickets per user, keeping every designer (so 0-scorers still
  // show) plus any assignee not on the current designer list.
  const tally = (subset: Ticket[]): Row[] => {
    const counts = new Map<string, number>();
    for (const d of designers) counts.set(d, 0);
    for (const t of subset) {
      const who = t.assignee?.trim();
      if (!who) continue;
      counts.set(who, (counts.get(who) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([user, count]) => ({ user, count }))
      .sort((a, b) => b.count - a.count || a.user.localeCompare(b.user));
  };

  const allTime = useMemo(() => tally(done), [done, designers]);
  const sprintRows = useMemo(
    () => tally(done.filter((t) => sprintOf(t) === selectedSprint)),
    [done, designers, selectedSprint, epochMs],
  );

  // Sprint dropdown: every sprint with a done ticket, plus the current one.
  const sprintOptions = useMemo(() => {
    const set = new Set<number>(done.map(sprintOf));
    set.add(currentSprint);
    return [...set].sort((a, b) => b - a);
  }, [done, currentSprint, epochMs]);

  if (loading) return <div className="placeholder">Loading leaderboard...</div>;

  return (
    <div className="leaderboard">
      {error && <div className="error">{error}</div>}
      <div className="leaderboard-tables">
        <ScoreTable title="All time" rows={allTime} showTotal />
        <ScoreTable
          title="By sprint"
          rows={sprintRows}
          showTotal
          header={
            <select
              value={selectedSprint}
              onChange={(e) => setSelectedSprint(Number(e.target.value))}
            >
              {sprintOptions.map((s) => (
                <option key={s} value={s}>
                  {s === currentSprint ? '\u{1F3AF} ' : ''}
                  {sprintLabel(s, epochMs)}
                </option>
              ))}
            </select>
          }
        />
      </div>
    </div>
  );
}

function ScoreTable({
  title,
  rows,
  header,
  showTotal,
}: {
  title: string;
  rows: Row[];
  header?: JSX.Element;
  // When set, a footer row sums the rows' counts (attributed done tickets).
  showTotal?: boolean;
}): JSX.Element {
  const total = rows.reduce((sum, r) => sum + r.count, 0);
  return (
    <div className="score-table">
      <div className="score-head">
        <h3>{title}</h3>
        {header}
      </div>
      {rows.length === 0 ? (
        <div className="muted">No designers yet.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th className="rank-col">#</th>
              <th>Designer</th>
              <th className="num-col">Done</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const medal = i < 3 && r.count > 0;
              return (
                <tr key={r.user} className={medal ? `top top-${i + 1}` : ''}>
                  <td className="rank-col">
                    {medal ? <i className="fas fa-medal" title={`#${i + 1}`} /> : i + 1}
                  </td>
                  <td>{r.user}</td>
                  <td className="num-col">{r.count}</td>
                </tr>
              );
            })}
          </tbody>
          {showTotal && (
            <tfoot>
              <tr className="score-total">
                <td className="rank-col" />
                <td>Total:</td>
                <td className="num-col">{total}</td>
              </tr>
            </tfoot>
          )}
        </table>
      )}
    </div>
  );
}
